import { NodeEnv } from '@mondaygpt/constants'
import { patchConsoleToErrorReporting } from './console-bridge'
import * as ErrorReporting from './error-reporting'
import { startTracing } from './tracing'

/**
 * App-facing error reporting: context-as-extras captures that also stamp the
 * current page. Thin layer over the ./error-reporting facade, which routes
 * everything to PostHog.
 */
class ErrorReportingService {
  captureException(error: Error | string, context?: Record<string, unknown>): void {
    ErrorReporting.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { page: this.getCurrentPage() },
      extra: context,
    })
  }

  captureMessage(
    message: string,
    level: ErrorReporting.SeverityLevel = 'info',
    context?: Record<string, unknown>
  ): void {
    ErrorReporting.captureMessage(message, {
      level,
      tags: { page: this.getCurrentPage() },
      extra: context,
    })
  }

  addBreadcrumb(message: string, category?: string, data?: Record<string, unknown>): void {
    ErrorReporting.addBreadcrumb({
      message,
      category: category || 'custom',
      data,
    })
  }

  private getCurrentPage(): string {
    try {
      return window.location.pathname + window.location.search
    } catch {
      return 'unknown'
    }
  }
}

export const errorReportingService = new ErrorReportingService()

let initialized = false

/**
 * Boot error reporting. Call after analytics (PostHog must be initialized for
 * captures to leave the browser; before that they are dropped).
 */
export const initializeErrorReporting = (): void => {
  if (initialized) return
  initialized = true

  const environment = import.meta.env.VITE_NODE_ENV || NodeEnv.Development
  const build = import.meta.env.VITE_APP_BUILD_NUMBER
  ErrorReporting.setTag('environment', environment)
  if (build) ErrorReporting.setTag('build', build)

  // Forward console.error/warn so caught errors become visible issues/logs
  patchConsoleToErrorReporting()
  startTracing({ environment, build })
}
